import { useMemo, useState } from 'react'
import { AnimatePresence, motion } from 'motion/react'
import { ArrowRight, Plus } from 'lucide-react'
import {
  COURSES,
  COURSE_LABELS,
  MEAL_SLOTS,
  MEAL_SLOT_LABELS,
  type Course,
  type Dish,
  type MealSlot,
} from '@shared/events'
import {
  dayPlate,
  effectiveMealsOfDay,
  planBySlot,
  plannedDishes,
  recipesById,
  type EffectiveMeal,
} from '@shared/plate'
import { addDays, weekdayLabel } from '@shared/time'
import { ElegirReceta, type Eleccion } from '../components/ElegirReceta'
import { Hoja } from '../components/Hoja'
import { Plato } from '../components/Plato'
import { Boton, Cabecera, Casilla, MostrarExtras, Tarjeta } from '../components/ui'
import { planificar } from '../lib/acciones'
import { useEvents, useToday } from '../lib/hooks'
import { useIrA } from '../lib/navegacion'
import { cursoLibre, mostrarCurso, nombreDePlato } from '../lib/platos'
import { useMostrarExtras } from '../lib/preferencias'
import { useSession } from '../lib/session'
import { deleteEvent, newEvent, saveEvent } from '../lib/sync'

type Eligiendo = { slot: MealSlot; para: 'plan' | 'comido' }

/**
 * La pantalla de inicio responde a la pregunta del día: qué toca comer.
 *
 * Marcar que se ha comido lo previsto cuesta un toque, que es el gesto que se
 * repite todos los días, y guarda un único evento con todos los platos de la
 * franja. Lo demás vive detrás de la tarjeta.
 */
export function Hoy() {
  const events = useEvents()
  const today = useToday()
  const { user } = useSession()
  const irA = useIrA()
  const { franjas } = useMostrarExtras()
  const [editando, setEditando] = useState<MealSlot | null>(null)
  const [eligiendo, setEligiendo] = useState<Eligiendo | null>(null)

  const meals = useMemo(() => effectiveMealsOfDay(today, events), [today, events])
  const recipes = useMemo(() => recipesById(events), [events])
  const plate = useMemo(() => dayPlate(today, events), [today, events])
  const manana = addDays(today, 1)
  const plan = useMemo(() => planBySlot(events), [events])

  const bySlot = new Map(meals.map((meal) => [meal.slot, meal]))
  // Lo que ya tiene algo se ve siempre, aunque su franja esté oculta.
  const visibles = MEAL_SLOTS.filter((slot) => franjas.includes(slot) || bySlot.has(slot))

  async function registrar(slot: MealSlot, dishes: Dish[], skipped = false): Promise<void> {
    if (!user) return
    await saveEvent(
      newEvent({
        type: 'meal_eaten',
        createdBy: user.id,
        payload: { dayKey: today, slot, dishes, skipped },
      }),
    )
  }

  const platosDe = (meal: EffectiveMeal | undefined): Dish[] =>
    (meal?.dishes ?? []).map(({ course, recipeId, freeText }) => ({ course, recipeId, freeText }))

  async function deshacer(slot: MealSlot): Promise<void> {
    const id = bySlot.get(slot)?.eatenEventId
    if (id) await deleteEvent(id)
    setEditando(null)
  }

  /** "Comimos otra cosa": se cambia ese plato y los demás quedan como estaban. */
  async function comimosOtraCosa(slot: MealSlot, course: Course, eleccion: Eleccion): Promise<void> {
    const resto = platosDe(bySlot.get(slot)).filter((dish) => dish.course !== course)
    setEligiendo(null)
    setEditando(null)
    await registrar(slot, [...resto, { course, ...eleccion }])
  }

  async function planificarHoy(slot: MealSlot, course: Course, eleccion: Eleccion): Promise<void> {
    if (!user) return
    setEligiendo(null)
    await planificar(events, user.id, today, slot, course, eleccion)
  }

  const ocupadosPlan = eligiendo ? plannedDishes(plan, today, eligiendo.slot).map((d) => d.course) : []
  const mealEditando = editando ? bySlot.get(editando) : undefined

  return (
    <>
      <Cabecera titulo={`${weekdayLabel(today)} ${Number(today.slice(8))}`} subtitulo="Lo que toca hoy en la mesa">
        <button
          type="button"
          onClick={() => irA('platos')}
          className="label-caps inline-flex min-h-11 items-center gap-1 px-1 text-muted-foreground outline-none hover:text-primary focus-visible:ring-2 focus-visible:ring-ring"
        >
          Planificar
          <ArrowRight className="h-3.5 w-3.5" />
        </button>
      </Cabecera>

      <div className="mt-4">
        <MostrarExtras />
      </div>

      <div className="mt-5 grid gap-2.5 sm:grid-cols-2">
        <AnimatePresence initial={false}>
          {visibles.map((slot, index) => (
            <motion.div
              key={slot}
              layout
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.96 }}
              transition={{ delay: index * 0.04, type: 'spring', stiffness: 320, damping: 30 }}
            >
              <TarjetaFranja
                slot={slot}
                meal={bySlot.get(slot)}
                recipes={recipes}
                onAbrir={() => setEditando(slot)}
                onAnadir={() => setEligiendo({ slot, para: 'plan' })}
                onMarcar={(meal) => (meal.eaten ? deshacer(slot) : registrar(slot, platosDe(meal)))}
              />
            </motion.div>
          ))}
        </AnimatePresence>
      </div>

      <div className="mt-4">
        <Plato plate={plate} />
      </div>

      <Tarjeta className="mt-3">
        <h2 className="font-display text-xl first-letter:uppercase">
          Mañana, {weekdayLabel(manana)} {Number(manana.slice(8))}
        </h2>
        <ul className="mt-3 space-y-2">
          {franjas.map((slot) => {
            const dishes = plannedDishes(plan, manana, slot)
            return (
              <li key={slot} className="grid grid-cols-[minmax(0,auto)_minmax(0,1fr)] items-baseline gap-3">
                <span className="label-caps text-muted-foreground">{MEAL_SLOT_LABELS[slot]}</span>
                <span
                  className={`truncate text-right text-sm ${dishes.length ? 'font-medium' : 'text-muted-foreground'}`}
                >
                  {dishes.length ? dishes.map((d) => nombreDePlato(d, recipes)).join(' · ') : 'sin poner'}
                </span>
              </li>
            )
          })}
        </ul>
        <button
          type="button"
          onClick={() => irA('platos')}
          className="mt-3 inline-flex min-h-11 items-center gap-1 text-sm font-medium text-primary outline-none hover:underline focus-visible:ring-2 focus-visible:ring-ring"
        >
          Planificar la semana
          <ArrowRight className="h-4 w-4" />
        </button>
      </Tarjeta>

      <AnimatePresence>
        {editando && (
          <Hoja
            key="opciones"
            titulo={MEAL_SLOT_LABELS[editando]}
            descripcion={
              mealEditando?.dishes.length
                ? mealEditando.dishes.map((d) => nombreDePlato(d, recipes)).join(' · ')
                : undefined
            }
            onCerrar={() => setEditando(null)}
          >
            <div className="grid gap-2">
              <Boton ancho onClick={() => setEligiendo({ slot: editando, para: 'comido' })}>
                Comimos otra cosa
              </Boton>
              <Boton
                ancho
                onClick={() => {
                  void registrar(editando, [], true)
                  setEditando(null)
                }}
              >
                No comimos en casa
              </Boton>
              <Boton ancho onClick={() => setEligiendo({ slot: editando, para: 'plan' })}>
                Añadir un plato a lo previsto
              </Boton>
              {mealEditando?.eaten && (
                <Boton ancho variante="peligro" onClick={() => void deshacer(editando)}>
                  Deshacer lo marcado
                </Boton>
              )}
            </div>
          </Hoja>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {eligiendo && (
          <ElegirReceta
            key="elegir"
            titulo={eligiendo.para === 'comido' ? '¿Qué comimos?' : `${MEAL_SLOT_LABELS[eligiendo.slot]} de hoy`}
            cursos={
              eligiendo.para === 'comido' ? COURSES : COURSES.filter((c) => !ocupadosPlan.includes(c))
            }
            cursoInicial={
              eligiendo.para === 'comido' ? 'segundo' : (cursoLibre(ocupadosPlan) ?? undefined)
            }
            onCerrar={() => setEligiendo(null)}
            onElegir={(eleccion, curso) => {
              const course = curso ?? 'segundo'
              if (eligiendo.para === 'comido') void comimosOtraCosa(eligiendo.slot, course, eleccion)
              else {
                setEditando(null)
                void planificarHoy(eligiendo.slot, course, eleccion)
              }
            }}
          />
        )}
      </AnimatePresence>
    </>
  )
}

function TarjetaFranja({
  slot,
  meal,
  recipes,
  onAbrir,
  onAnadir,
  onMarcar,
}: {
  slot: MealSlot
  meal: EffectiveMeal | undefined
  recipes: ReturnType<typeof recipesById>
  onAbrir: () => void
  onAnadir: () => void
  onMarcar: (meal: EffectiveMeal) => void
}) {
  const etiqueta = MEAL_SLOT_LABELS[slot].toLowerCase()

  if (!meal) {
    return (
      <motion.button
        type="button"
        onClick={onAnadir}
        whileTap={{ scale: 0.985 }}
        className="flex w-full flex-col rounded-3xl border border-dashed border-border px-4 py-4 text-left outline-none transition-colors hover:border-primary/50 hover:bg-accent/60 focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="label-caps text-muted-foreground">{MEAL_SLOT_LABELS[slot]}</span>
        <span className="mt-2 inline-flex items-center gap-1.5 text-base text-muted-foreground">
          <Plus className="h-4 w-4" strokeWidth={2.4} />
          añadir plato
        </span>
      </motion.button>
    )
  }

  const conCurso = mostrarCurso(meal.dishes)

  return (
    <div
      className={`flex items-start gap-1 rounded-3xl border py-2 pl-4 pr-1.5 transition-colors duration-300 ${
        meal.eaten ? 'border-sage/45 bg-filled' : 'border-border/70 bg-card'
      }`}
    >
      <button
        type="button"
        onClick={onAbrir}
        className="min-w-0 flex-1 rounded-2xl py-2 text-left outline-none focus-visible:ring-2 focus-visible:ring-ring"
      >
        <span className="label-caps flex items-center gap-2 text-muted-foreground">
          {MEAL_SLOT_LABELS[slot]}
          {meal.eaten && !meal.skipped && <span className="text-sage-deep">· comido</span>}
        </span>
        {meal.skipped ? (
          <span className="mt-1.5 block text-lg text-muted-foreground">Sin comer en casa</span>
        ) : (
          <ul className="mt-1.5 space-y-0.5">
            {meal.dishes.map((dish) => (
              <li key={dish.course} className="flex items-baseline gap-2">
                {conCurso && (
                  <span className="w-[4.6rem] shrink-0 text-[0.7rem] font-medium uppercase tracking-wider text-muted-foreground">
                    {COURSE_LABELS[dish.course]}
                  </span>
                )}
                <span className="min-w-0 truncate text-lg font-medium leading-snug">
                  {nombreDePlato(dish, recipes)}
                </span>
              </li>
            ))}
          </ul>
        )}
      </button>
      {!meal.skipped && meal.dishes.length > 0 && (
        <Casilla
          marcada={meal.eaten}
          label={meal.eaten ? `Deshacer ${etiqueta}` : `Marcar ${etiqueta} como comido`}
          onClick={() => onMarcar(meal)}
        />
      )}
    </div>
  )
}
